/**
 * @ngdoc directive
 * @name flowContainer
 * @module flowingly.runner.flow
 * @description  This component is used to gruop the content that is displayed when the flow is expanded..
 * @usage
 * ```
     <flow-container ng-show="$ctrl.expand" flow="$ctrl.flow"  on-comments-click="$ctrl.OnCommentsClick()" is-mobile="$ctrl.isMobile"></flow-container>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Flow+Components
 * ### Properties
 * #### Inputs
 * * flow: the flow data to display (JSON)
 * * isMobile: show mobile view if set
 * * userId: userid of the logged in user (SMEG)
 */

import { FormGen } from '@Client/@types/formGen';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { RunnerFlowsFormatterService } from '@Client/runner.services/flows.formatter';
import angular, {
  ILocationService,
  IQService,
  IRootScopeService,
  ITimeoutService
} from 'angular';
import RunnerFlowService from '../runner.flow/runner.flow.service';
import StepService from '@Client/runner.services/step.service';
import IFormattedFlow from '@Client/interfaces/IFormattedFlow';
import IFormattedStep from '@Client/interfaces/IFormattedStep';
import { FormFieldType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import FieldDataSource from '@Shared.Angular/@types/fieldDataSource';
import IntegrationState from '@Shared.Angular/@types/core/contracts/queryModel/webhook/integrationState';
import { toCamelCaseDeep } from '@Shared.Angular/flowingly.services/case-converter';
import { Guid } from '@Shared.Angular/@types/guid';

class FlowContainerController implements angular.IController {
  public static $inject = [
    '$rootScope',
    '$q',
    '$timeout',
    'lodashService',
    'flowApiService',
    'fgFileListService',
    'runnerFlowsFormatter',
    'runnerFlowService',
    'flowinglyDiagramService',
    'goService',
    'pubsubService',
    '$location',
    'flowinglyConstants',
    'appInsightsService',
    'stepService'
  ];

  public constructor(
    private $rootScope: IRootScopeService,
    private $q: IQService,
    private $timeout: ITimeoutService,
    private lodashService: Lodash,
    private flowApiService: FlowApiService,
    private fgFileListService: FormGen.FgFileListService,
    private runnerFlowsFormatter: RunnerFlowsFormatterService,
    private runnerFlowService: RunnerFlowService,
    private flowinglyDiagramService: SharedAngular.FlowinglyDiagramService,
    private goService: GoJS,
    private pubsubService: SharedAngular.PubSubService,
    private $location: ILocationService,
    private flowinglyConstants: SharedAngular.FlowinglyConstants,
    private appInsightsService: SharedAngular.AppInsightsService,
    private stepService: StepService
  ) {}

  // from bindings
  public flow: IFormattedFlow;
  public onSelectedStepChange: ({
    stepId,
    isWaitingForYou,
    isCompleted
  }: {
    stepId: Guid;
    isWaitingForYou: boolean;
    isCompleted: boolean;
  }) => void;

  // actual vars
  private selectedStep = {} as IFormattedStep;
  private previousSelectedStepSchemaFields: IFormField[] = [];
  private changingStep = false;
  private disableFooterButtonsboolean = false;
  private showDoneButton: boolean;
  public displaySelectedStepTaskDetails = false;
  private selectedStepTask: any = {};

  private subscriberId = 'flowContainer';
  public $onChanges() {
    this.initialize();
  }

  public $onDestroy(): void {
    this.pubsubService.unsubscribeAll(this.subscriberId);
  }

  private hasStarted() {
    return this.runnerFlowService.hasStarted;
  }

  public selectedStepIsProcessingIntegration() {
    if (
      this.selectedStep != null &&
      this.selectedStep.integrationState != null
    ) {
      return (
        this.selectedStep.integrationState.state === IntegrationState.Processing
      );
    }
    return false;
  }

  private initialize() {
    const { lodashService, $rootScope } = this;

    (this.flow as any).startedByUserAvatarUrl = null;
    if (this.flow.steps) {
      // Check whether the currently selected step is still present and incomplete in the
      // refreshed flow data (e.g. after a SignalR update). If it is, we keep the user on
      // that step — don't interrupt them mid-fill just because another parallel user acted.
      const selectedStepIsStillValid =
        this.selectedStep?.id &&
        this.flow.steps.some(
          (s) => s.id === this.selectedStep.id && !s.isCompleted
        );

      lodashService.forEach(this.flow.steps, (step: any) => {
        step.actorAssignedAvatarUrl = null;
        step.completedByAvatarUrl = null;

        if (step.isSelectedStep && !selectedStepIsStillValid) {
          this.previousSelectedStepSchemaFields =
            (this.selectedStep as any)?.schema?.fields ?? [];
          this.selectedStep = step;
          this.notifySelectedStepChanged();
          this.injectDynamicDropDownsOptions();
          this.dynamicallySelectStepTask();
        }

        if (step.id === this.selectedStep?.id && selectedStepIsStillValid) {
          // Keep selectedStep pointing at the fresh step object from the updated flow
          // so that bindings reflect up-to-date server data without changing which step
          // the user is on.
          this.previousSelectedStepSchemaFields =
            (this.selectedStep as any)?.schema?.fields ?? [];
          this.selectedStep = step;
          this.notifySelectedStepChanged();
          this.injectDynamicDropDownsOptions();
        }

        // [FLOW-5585] Reorder Step Tasks if the step is not completed. If completed
        // then that means this component is used for displaying history. Don't have to apply
        // sorting in history.
        if (!step.isCompleted && step.stepTasks != null) {
          step.stepTasks = this.runnerFlowsFormatter.reorderStepTasks(
            step.stepTasks
          );
        }
      });
    }

    switch ($rootScope.previousRoute) {
      case '/flowsin':
        if (this.flow.isWaitingForYou) {
          this.showDoneButton = this.haveIncompleteSteps();
        }
        break;

      case '/flowstodo':
        this.showDoneButton = this.haveIncompleteSteps();
        break;

      default:
        if (this.flow.isWaitingForYou) {
          this.showDoneButton = this.haveIncompleteSteps();
        }
    }

    this.pubsubService.subscribe(
      'STEP_TASK_CANCELLED',
      (event, data) => {
        this.resetStepTaskSelection(data);
      },
      this.subscriberId
    );

    this.pubsubService.subscribe(
      'STEP_TASK_UPDATED',
      (event, stepTask: any) => {
        if (stepTask != null && this.selectedStepTask != null) {
          if (
            this.displaySelectedStepTaskDetails &&
            stepTask.id === this.selectedStepTask.id
          ) {
            this.stepTaskSelectionChanged(stepTask);
          }
        }
      },
      this.subscriberId
    );

    // Subscribe to the step complete to clear any selected step task so that when the flow
    // is progressed to next step the selection is not retained.
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_COMPLETE_STEP',
      (event, data) => {
        this.clearStepTaskSelection();
      },
      this.subscriberId
    );

    this.scrollToTop();

    // flow could be loaded either from a link, or by tapping a flow in a list, or by refresh browser window
    const flowIdentifier = this.flow?.flowIdentifier || '';
    this.appInsightsService.trackMetricIfTimerExist(
      'flowLoadFromExternalLink',
      { flowIdentifier }
    );
    this.appInsightsService.trackMetricIfTimerExist('flowSelectedFromList', {
      flowIdentifier
    });
    this.appInsightsService.trackMetricIfTimerExist(
      'stepFormTransitionStarted',
      this.getPropsForAppInsights()
    );
  }

  private clearStepTaskSelection() {
    this.$location.search('stepTask', null); // Remove the step task Id from the route.
    this.selectedStepTask = null;
    this.displaySelectedStepTaskDetails = false;
  }

  /*
   * If a step task is cancelled then of any users that has the same step task selected
   * needs to have their view reset as the step task is no longer valid. If the step task
   * that was deleted and not the current selected one then there is nothing to be reset.
   */
  private resetStepTaskSelection(data) {
    if (data && data.stepTaskId && this.selectedStepTask) {
      if (this.selectedStepTask.id === data.stepTaskId) {
        this.clearStepTaskSelection();
      }
    }
  }

  /*
   * A user could end up here redirected from an email button click, in that case we might have a
   * stepTaskId in the query string. If its is found in the URL then we need to select a StepTask
   * that matches this ID.
   */
  private dynamicallySelectStepTask() {
    // TODO: SS - We should ideally have the name of the query string "stepTask" in a config or somewhere else.
    // Get step task id from query string if it exists.
    const stepTaskIdToSelect = this.$location.search().stepTask;
    if (stepTaskIdToSelect != null) {
      if (this.selectedStep.stepTasks != null) {
        const stepTask = this.selectedStep.stepTasks.find(
          (tsk) => tsk.id === stepTaskIdToSelect
        );
        if (stepTask != null) {
          this.stepTaskSelectionChanged(stepTask);
        }
      }
    }
  }

  private stepTaskSelectionChanged(stepTask) {
    // Unselect any steps or step tasks first.
    if (this.flow.steps != null) {
      for (const step of this.flow.steps) {
        step.isSelectedStep = false;
        if (step.stepTasks != null) {
          for (const task of step.stepTasks) {
            (task as any).isSelected = false;
          }
        }
      }
    }

    // Change selection to the new step task.
    stepTask.isSelected = true;
    this.selectedStepTask = stepTask;
    this.displaySelectedStepTaskDetails = true;
  }

  private injectDynamicDropDownsOptions() {
    // save the NotifyFields property to schema, then in FormGen side, we know which fields need to notify
    (this.selectedStep as any).schema.fields.forEach((s) => {
      if (s.type !== 'submit') {
        const matchField = this.selectedStep.fields.find(
          (f) => f.name === s.name
        );

        if (matchField) {
          if (matchField.notifyFields) {
            s.notifyFields = matchField.notifyFields;
          }

          if (s.dataSource === FieldDataSource.DATABASE && matchField.options) {
            s.options = toCamelCaseDeep(JSON.parse(matchField.options));
          } else if (s.dataSource === FieldDataSource.DATABASE) {
            s.options = [];
          } else if (s.type === 'table' && matchField.options) {
            s.options = toCamelCaseDeep(JSON.parse(matchField.options));
          }
        }

        this.preserveFilteredOptionsFromPreviousSchema(s);
      }
    });
  }

  private preserveFilteredOptionsFromPreviousSchema(schemaField: IFormField) {
    const previousSchemaField = this.previousSelectedStepSchemaFields.find(
      (f) => f.name === schemaField.name
    );

    if (previousSchemaField?.filteredOptions) {
      schemaField.filteredOptions = previousSchemaField.filteredOptions;
    }
  }

  private haveIncompleteSteps() {
    return this.flow.steps.some((s) => s.isCompleted === 0);
  }

  private unSelectAnyStepTasks() {
    this.clearStepTaskSelection();

    if (this.flow.steps != null) {
      for (const step of this.flow.steps) {
        if (step.stepTasks != null) {
          for (const stepTask of step.stepTasks) {
            (stepTask as any).isSelected = false;
          }
        }
      }
    }
  }

  private onSelectStep(stepId) {
    // The user has clicked on a step so we need to unselect any step tasks.
    this.unSelectAnyStepTasks();

    if (stepId !== this.selectedStep.id) {
      this.changingStep = true;

      this.saveProgress().then(() => {
        for (const s of this.flow.steps) {
          s.isSelectedStep = false;

          if (s.id === stepId) {
            s.isSelectedStep = true;
            this.selectedStep = s as any;
            this.notifySelectedStepChanged();
            this.updateDisplayOnDivergeStepsSwitch(this.selectedStep as any);
            this.injectDynamicDropDownsOptions();

            if ($('li[aria-controls=tabstrip-3]').hasClass('k-state-active')) {
              this.flowinglyDiagramService.generateProcessModel({
                flow: this.flow,
                applyAvatar: false,
                modelCustomArgs: {
                  scrollMode: this.goService.Diagram.DocumentScroll
                }
              });
            }
          }
        }

        this.$timeout(() => {
          this.changingStep = false;
        }, 500);
      });
    }
  }

  private updateDisplayOnDivergeStepsSwitch(selectedStep: IFormattedStep) {
    if ((selectedStep as any).$data) {
      const fieldFiles = this.fgFileListService.getFilesForStep(
        selectedStep.id
      );
      for (const field of selectedStep.fields) {
        if (field.type === FormFieldType.FILE_UPLOAD) {
          if (fieldFiles) {
            (selectedStep as any).$data[field.name] = fieldFiles
              .filter((group) => group.fieldName === field.name)
              .flatMap((group) => group.files)
              .map((file) => file.id)
              .toString();
            field.value = (selectedStep as any).$data[field.name];
          } else {
            field.value = null;
          }
        } else if (field.type === FormFieldType.TASK_LIST) {
          const schemaField = selectedStep.schema.fields.find(
            (f) => f.name === field.name
          );
          const stepFieldData = (selectedStep as any).$data[field.name];
          (field as any).value = this.stepService
            .getTaskListValues(schemaField, stepFieldData)
            .map((option) => {
              return { key: option.key, value: option.value.toString() };
            });
        } else if (field.type === FormFieldType.MULTISELECT_LIST) {
          const schemaField = selectedStep.schema.fields.find(
            (f) => f.name === field.name
          );
          const stepFieldData = (selectedStep as any).$data[field.name];
          (field as any).value = this.stepService
            .getMultiSelectValues(schemaField, stepFieldData)
            .map((option) => {
              return { key: option.key, value: option.value.toString() };
            });
        } else {
          field.value = (selectedStep as any).$data[field.name];
        }
      }
    }
  }

  private notifySelectedStepChanged(): void {
    if (this.onSelectedStepChange && this.selectedStep?.id) {
      this.onSelectedStepChange({
        stepId: this.selectedStep.id,
        isWaitingForYou: this.selectedStep.isWaitingForYou === 1,
        isCompleted: this.selectedStep.isCompleted === 1
      });
    }
  }

  private saveProgress() {
    if (
      !this.selectedStep ||
      !this.selectedStep.isWaitingForYou ||
      !(this.selectedStep as any).$data
    ) {
      return this.$q.when();
    }

    const formData = this.stepService.getFormDataWithoutValidating(
      this.selectedStep
    );

    this.runnerFlowsFormatter.encodeFlowFormData(
      formData,
      (this.selectedStep as any).schema.fields,
      true
    );
    const dataToSave = this.stepService.appendTimezoneOffsetToDates(
      formData,
      (this.selectedStep as any).schema
    );

    return this.flowApiService.saveFormProgress(
      this.selectedStep.flowId,
      this.selectedStep.id,
      dataToSave,
      this.fgFileListService.getFilesForStep(this.selectedStep.id)
    );
  }

  private scrollToTop() {
    this.$timeout(() => {
      const paneRightViewTopMarker = document.getElementById(
        'pane-right-scroll-view-top-marker'
      );
      if (paneRightViewTopMarker) {
        paneRightViewTopMarker.scrollIntoView();
      }
    });
  }

  private getPropsForAppInsights() {
    const { flowIdentifier, steps, finalisedReason, percentageComplete } =
      this.flow ?? {};
    const selectedStep = steps?.find((step) => step.isSelectedStep === true);
    const isLastStep = percentageComplete === 100;
    const transitionToStep = isLastStep ? undefined : selectedStep?.name;
    return {
      flowIdentifier,
      percentageComplete,
      isLastStep,
      transitionToStep,
      finalisedReason
    };
  }
}
class FlowContainer implements angular.IComponentOptions {
  public bindings = {
    flow: '<',
    isMobile: '<', //show mobile view if set
    userId: '<', //userid of the logged in user
    onCommentsClick: '&', //show comments tab on click
    onSelectedStepChange: '&'
  };

  public templateUrl =
    'Client/runner.flow/runner.flow.container/runner.flow.container.tmpl.html';

  public controller = FlowContainerController;
}

angular
  .module('flowingly.runner.flow')
  .component('flowContainer', new FlowContainer());
